import React, { useState, useRef, useEffect, useMemo } from 'react';
import { NavLink, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { getTutorProfile } from '../api/supabaseAPI';
// ✨ Reverted to use local file for full SVG control
import { 
  SidebarRight, 
  Home, 
  Play, 
  BookOpen, 
  Brain, 
  Monitoring,
  LineChart,
  SettingsGear,
  Users,
  AcademicCap,
  Trophy,
  Store
} from './icons/GeistIcons';
import { getPublicSetting } from '../api/supabaseAPI';
import UserProfilePopover from './UserProfilePopover';
import '../styles/Sidebar.css';

const R2_PUBLIC_BASE_URL_ENV = (import.meta.env.VITE_R2_PUBLIC_BASE_URL || '').replace(/\/$/, '');

const Sidebar = ({ onLogout, hasCompletedTest, userData }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { language: routeLanguage } = useParams(); // Get language from route if available
  const avatarImgRef = useRef(null);
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const forcedSidebarMode = searchParams.get('sidebar');
  const queryLanguage = searchParams.get('lang');
  const isTutorWorkspacePath = location.pathname.startsWith('/tutor/workspace');
  const isTutorWorkspace = forcedSidebarMode
    ? forcedSidebarMode === 'tutor'
    : isTutorWorkspacePath;
  const tutorLanguage = routeLanguage || queryLanguage || userData?.settings?.language || 'en_us';

  // ✨ Fetch Tutor Profile if in workspace mode
  const { data: tutorProfile } = useQuery({
    queryKey: ['tutorProfile', userData?.id, tutorLanguage],
    queryFn: () => getTutorProfile(userData?.id, tutorLanguage),
    enabled: !!isTutorWorkspace && !!userData?.id && !!tutorLanguage,
    staleTime: 1000 * 60 * 5,
  });

  const [isAvatarLoaded, setIsAvatarLoaded] = useState(false);
  const [isAvatarError, setIsAvatarError] = useState(false);
  const [avatarCacheBust, setAvatarCacheBust] = useState(() => localStorage.getItem('avatarCacheBust') || '');
  
  // Persist collapsed state
  const [isCollapsed, setIsCollapsed] = useState(() => {
    return localStorage.getItem('sidebarCollapsed') === 'true';
  });

  // ✨ Update CSS variable for sidebar width
  useEffect(() => {
    const width = isCollapsed ? '72px' : '260px';
    document.documentElement.style.setProperty('--sidebar-width', width);
  }, [isCollapsed]);

  const [showProfile, setShowProfile] = useState(false);

  const toggleSidebar = () => {
    const newState = !isCollapsed;
    setIsCollapsed(newState);
    localStorage.setItem('sidebarCollapsed', newState);
  };

  const studentNavItems = [
    {
      path: '/introduction',
      label: t('header.introduction', 'Introduction'),
      icon: <Home size={20} />,
      requiresAuth: false
    },
    {
      path: '/initial-test',
      label: t('header.initialTest', 'Initial Test'),
      icon: <Play size={20} />,
      hidden: hasCompletedTest // Hide if test completed
    },
    {
      path: '/courses',
      label: t('header.courses', 'Courses'),
      icon: <BookOpen size={20} />,
      hidden: !hasCompletedTest
    },
    {
      path: '/practice',
      label: t('header.practice', 'Practice'),
      icon: <Brain size={20} />,
      hidden: !hasCompletedTest
    },
    {
      path: '/overall',
      label: t('header.overall', 'Overall'),
      icon: <LineChart size={20} />,
      hidden: !hasCompletedTest
    },
    {
      path: '/records',
      label: t('header.records', 'Records'),
      icon: <Monitoring size={20} />,
      hidden: !hasCompletedTest
    },
    {
      path: '/leaderboard',
      label: t('header.leaderboard', 'Leaderboard'),
      icon: <Trophy size={20} />,
      hidden: !hasCompletedTest
    },
    {
      path: '/tutors',
      label: t('header.findTutor', 'Find a Tutor'),
      icon: <Users size={20} />,
      hidden: !hasCompletedTest
    },
    {
      path: '/friends',
      label: t('header.friends', 'Friends'),
      icon: <Users size={20} />,
      hidden: !hasCompletedTest
    },
    {
      path: '/tutor-application',
      label: t('header.tutorApplication', 'Tutor Application'),
      icon: <AcademicCap size={20} />,
      hidden: !hasCompletedTest
    },
    // ✨ Settings moved to the bottom of the nav list
    {
      path: '/shop',
      label: t('header.shop', 'Shop'),
      icon: <Store size={20} />,
      hidden: !hasCompletedTest
    },
    {
      path: '/profile',
      label: t('header.profile', 'Settings'),
      icon: <SettingsGear size={20} />,
      requiresAuth: true,
      isBottom: true // Flag to push this item to the bottom
    }
  ];

  const tutorNavItems = [
    {
      path: `/tutor/workspace/${tutorLanguage}`,
      label: 'Dashboard',
      icon: <Home size={20} />, // Fallback icon
      exact: true
    },
    {
      path: `/tutor/workspace/${tutorLanguage}/profile`,
      label: 'Tutor Profile',
      icon: <SettingsGear size={20} />,
      isBottom: true
    }
  ];

  const navItems = isTutorWorkspace ? tutorNavItems : studentNavItems;

  // ✨ Switch user data display based on mode
  const displayUser = isTutorWorkspace && tutorProfile ? {
    username: tutorProfile.display_name || tutorProfile.full_name || userData?.username,
    email: tutorProfile.headline || 'No headline',
    avatar: tutorProfile.avatar,
    background: tutorProfile.background,
    isTutor: true
  } : userData;

  const username = displayUser?.username || 'User';
  const email = displayUser?.email || '';

  const { data: r2PublicBaseUrl } = useQuery({
    queryKey: ['public_settings', 'r2_public_base_url'],
    queryFn: () => getPublicSetting('r2_public_base_url'),
    staleTime: 1000 * 60 * 30,
    refetchOnMount: false,
  });
  const publicBaseUrl = useMemo(() => {
    const rawBaseUrl = R2_PUBLIC_BASE_URL_ENV || r2PublicBaseUrl || '';
    return rawBaseUrl ? rawBaseUrl.replace(/\/$/, '') : '';
  }, [r2PublicBaseUrl]);
  const isExternalUrl = useMemo(() => (value) => /^https?:\/\//i.test(value || ''), []);
  
  const avatarUrl = useMemo(() => {
    const avatarPath = displayUser?.avatar;
    const googleAvatar = userData?.googleAvatarUrl; // Keep fallback to user's google avatar if tutor avatar is empty? 
    // Actually prompt says "default as empty". If empty, maybe show default avatar or user's google avatar?
    // Let's stick to logic: if tutor avatar is set, use it. If not, use default.
    
    // Priority 1: Stored avatar
    if (avatarPath) {
        if (isExternalUrl(avatarPath)) return avatarPath;
        if (!publicBaseUrl) return avatarPath;
        const baseUrl = `${publicBaseUrl}/${avatarPath}`;
        return avatarCacheBust ? `${baseUrl}?v=${avatarCacheBust}` : baseUrl;
    }

    // Priority 2: Google Avatar (Only for student mode, or if we want to fallback)
    // For Tutor mode, if avatar is null, it should probably be default to encourage setting one.
    if (!displayUser?.isTutor && googleAvatar) {
        return googleAvatar;
    }

    // Priority 3: Default Avatar
    return '/Resources/imagine/default_avatar/default_avatar.jpeg';
  }, [avatarCacheBust, publicBaseUrl, displayUser?.avatar, displayUser?.isTutor, userData?.googleAvatarUrl, isExternalUrl]);

  const backgroundUrl = useMemo(() => {
    const backgroundPath = displayUser?.background;
    if (!backgroundPath) return '';
    if (!publicBaseUrl) return backgroundPath;
    return `${publicBaseUrl}/${backgroundPath}`;
  }, [publicBaseUrl, displayUser?.background]);

  const showAvatarSkeleton = !isAvatarLoaded;

  useEffect(() => {
    // Reset loaded state when URL changes
    setIsAvatarLoaded(false);
    setIsAvatarError(false);
  }, [avatarUrl]);

  useEffect(() => {
    if (!avatarImgRef.current) return;
    if (avatarImgRef.current.complete && avatarImgRef.current.naturalWidth > 0) {
      setIsAvatarLoaded(true);
    }
  }, [avatarUrl]);


  useEffect(() => {
    const syncCacheBust = (event) => {
      if (event?.type === 'storage' && event.key !== 'avatarCacheBust') return;
      const nextValue = localStorage.getItem('avatarCacheBust') || '';
      setAvatarCacheBust(nextValue);
    };
    window.addEventListener('storage', syncCacheBust);
    window.addEventListener('avatar-cache-bust', syncCacheBust);
    return () => {
      window.removeEventListener('storage', syncCacheBust);
      window.removeEventListener('avatar-cache-bust', syncCacheBust);
    };
  }, []);

  return (
    <aside className={`sidebar ${isCollapsed ? 'collapsed' : ''}`}>
      {/* Header */}
      <div className="sidebar-header">
        <div className="sidebar-logo">
          TheraLingua AI
        </div>
        <button 
            className="sidebar-toggle" 
            onClick={toggleSidebar}
            title={isCollapsed ? "Expand Sidebar" : "Collapse Sidebar"}
        >
          <SidebarRight size={20} />
        </button>
      </div>

      {/* Navigation */}
      <nav className="sidebar-nav">
        {navItems.filter(item => !item.hidden).map((item) => {
          // Logic for active state
          const isActive = item.exact 
            ? location.pathname === item.path
            : location.pathname.startsWith(item.path);

          return (
            <button
              key={item.path}
              className={`nav-item ${isActive ? 'active' : ''}`}
              onClick={() => {
                if (item.action) {
                  item.action();
                  return;
                }
                let targetPath = item.path;
                if (item.path === '/friends') {
                  const savedTab = localStorage.getItem('last_active_tab_friends');
                  if (savedTab) targetPath += `?tab=${savedTab}`;
                } else if (item.path === '/profile') {
                  const savedTab = localStorage.getItem('last_active_tab_profile');
                  if (savedTab) targetPath += `?tab=${savedTab}`;
                } else if (isTutorWorkspace && targetPath.startsWith('/tutor/workspace/')) {
                  const nextParams = new URLSearchParams();
                  nextParams.set('sidebar', 'tutor');
                  nextParams.set('lang', tutorLanguage);
                  targetPath += `?${nextParams.toString()}`;
                }
                navigate(targetPath);
              }}
              title={isCollapsed ? item.label : ''}
              style={item.isBottom ? { marginTop: 'auto' } : {}} // ✨ Push to bottom
            >
              {item.icon}
              <span className="nav-text">{item.label}</span>
            </button>
          );
        })}
      </nav>

      {/* Footer / User Profile */}
      <div className="sidebar-footer">
        <button 
            className="user-trigger" 
            onClick={() => setShowProfile(!showProfile)}
            title={isCollapsed ? username : ''}
        >
          <div className="user-avatar">
            {showAvatarSkeleton && (
              <div className={`user-avatar-skeleton ${isAvatarLoaded ? 'is-hidden' : ''}`} />
            )}
            <img
              className={`user-avatar-img ${isAvatarLoaded ? 'is-visible' : ''}`}
              src={avatarUrl}
              alt={t('profilePage.avatarAlt', 'User avatar')}
              ref={avatarImgRef}
              key={avatarUrl}
              onLoad={() => setIsAvatarLoaded(true)}
              onError={(e) => {
                setIsAvatarError(true);
                e.target.src = '/Resources/imagine/default_avatar/default_avatar.jpeg';
              }}
            />
          </div>
          <div className="user-info">
            <div className="user-name">{username}</div>
            <div className="user-email-text">{email}</div>
          </div>
        </button>
      </div>

      {/* Popover */}
      {showProfile && (
        <UserProfilePopover 
            userData={{
              ...userData, // Keep original user data structure
              ...displayUser, // Override with tutor profile if active
              avatarUrl, // Use computed URL
              backgroundUrl, // Use computed URL
              isAvatarLoaded,
              isAvatarError,
              onAvatarLoad: () => setIsAvatarLoaded(true),
              onAvatarError: () => setIsAvatarError(true),
            }} 
            onLogout={onLogout} 
            onClose={() => setShowProfile(false)}
            isCollapsed={isCollapsed}
            onNavigate={navigate}
            isTutorMode={isTutorWorkspace} // ✨ Pass flag to popover
            currentLanguage={tutorLanguage}
        />
      )}
    </aside>
  );
};

export default Sidebar;
